import React, { useState, useEffect, useMemo } from 'react';
import {
  ChartBarIcon,
  DocumentChartBarIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import Button from '../../components/UI/Button';
import StatsCard from '../../components/UI/StatsCard';
import ClientAnalytics from '../../components/Reports/ClientAnalytics';
import RevenueAnalytics from '../../components/Reports/RevenueAnalytics';
import IngredientAnalytics from '../../components/Reports/IngredientAnalytics';
import DietAnalytics from '../../components/Reports/DietAnalytics';
import CountReport from '../../components/Reports/CountReport';
import FilingReport from '../../components/Reports/FilingReport';
import analyticsService, { OverviewStats } from '../../services/analyticsService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { usePermission } from '../../hooks/usePermission';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';

type ReportTab = 'overview' | 'clients' | 'revenue' | 'ingredients' | 'diets' | 'menu-preparation' | 'daily-production' | 'count' | 'filing';

const ReportsPage: React.FC = () => {
  const { hasPermission, isAdmin } = usePermission();
  const { myPermissions } = useAdminPermissions();
  const [activeTab, setActiveTab] = useState<ReportTab>('overview');

  // Debug logging
  useEffect(() => {
    console.log('[ReportsPage] Component mounted/updated');
    console.log('[ReportsPage] isAdmin:', isAdmin);
    console.log('[ReportsPage] myPermissions:', myPermissions);
    console.log('[ReportsPage] hasPermission(REPORTS_FILING, VIEW):', hasPermission('REPORTS_FILING', 'VIEW'));
    console.log('[ReportsPage] hasPermission(REPORTS_CHEF_COUNT, VIEW):', hasPermission('REPORTS_CHEF_COUNT', 'VIEW'));
  }, [isAdmin, myPermissions, hasPermission]);
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    to: new Date().toISOString().split('T')[0], // today
  });
  const [overviewStats, setOverviewStats] = useState<OverviewStats | null>(null);
  const [loading, setLoading] = useState(true);

  // Load overview stats
  useEffect(() => {
    const loadOverviewStats = async () => {
      try {
        setLoading(true);
        const stats = await analyticsService.getOverviewStats(dateRange);
        setOverviewStats(stats);
      } catch (error) {
        console.error('Failed to load overview stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOverviewStats();
  }, [dateRange]);

  // Convert API stats to StatsCard format
  const getStatsCardData = () => {
    if (!overviewStats) return [];

    return [
      {
        id: 'total-clients',
        title: 'Total Clients',
        value: overviewStats?.totalClients?.value ?? 0,
        change: {
          value: overviewStats?.totalClients?.change ?? 0,
          type: (overviewStats?.totalClients?.type as any) ?? 'neutral',
          period: 'from last month'
        },
        icon: <UserGroupIcon className="w-6 h-6" />,
        color: 'blue' as const,
        onClick: () => window.location.href = '/clients',
      },
      {
        id: 'total-ingredients',
        title: 'Total Ingredients',
        value: overviewStats?.totalIngredients?.value ?? 0,
        change: {
          value: overviewStats?.totalIngredients?.change ?? 0,
          type: (overviewStats?.totalIngredients?.type as any) ?? 'neutral',
          period: 'from last month'
        },
        icon: <DocumentChartBarIcon className="w-6 h-6" />,
        color: 'green' as const,
        onClick: () => window.location.href = '/setup/ingredients',
      },
      {
        id: 'total-diets',
        title: 'Total Diets',
        value: overviewStats?.totalDiets?.value ?? 0,
        change: {
          value: overviewStats?.totalDiets?.change ?? 0,
          type: (overviewStats?.totalDiets?.type as any) ?? 'neutral',
          period: 'from last month'
        },
        icon: <ChartBarIcon className="w-6 h-6" />,
        color: 'yellow' as const,
        onClick: () => window.location.href = '/setup/diets',
      },
      {
        id: 'total-recipes',
        title: 'Total Recipes',
        value: overviewStats?.totalRecipes?.value ?? 0,
        change: {
          value: overviewStats?.totalRecipes?.change ?? 0,
          type: (overviewStats?.totalRecipes?.type as any) ?? 'neutral',
          period: 'from last month'
        },
        icon: <CurrencyDollarIcon className="w-6 h-6" />,
        color: 'purple' as const,
        onClick: () => window.location.href = '/setup/recipes',
      },
    ];
  };

  // Define all tabs with their permission requirements
  const allTabs = [
    {
      id: 'overview',
      label: 'Overview',
      icon: <ChartBarIcon className="w-5 h-5" />,
      requiresPermission: 'REPORTS_OVERVIEW'
    },
    {
      id: 'clients',
      label: 'Client Analytics',
      icon: <UserGroupIcon className="w-5 h-5" />,
      requiresPermission: 'REPORTS_CLIENTS'
    },
    {
      id: 'revenue',
      label: 'Revenue Analytics',
      icon: <CurrencyDollarIcon className="w-5 h-5" />,
      requiresPermission: 'REPORTS_REVENUE'
    },
    {
      id: 'ingredients',
      label: 'Ingredient Analytics',
      icon: <DocumentChartBarIcon className="w-5 h-5" />,
      requiresPermission: 'REPORTS_INGREDIENTS'
    },
    {
      id: 'diets',
      label: 'Diet Analytics',
      icon: <ChartBarIcon className="w-5 h-5" />,
      requiresPermission: 'REPORTS_DIETS'
    },
    {
      id: 'count',
      label: 'Chef Count',
      icon: <DocumentChartBarIcon className="w-5 h-5" />,
      requiresPermission: 'REPORTS_CHEF_COUNT'
    },
    {
      id: 'filing',
      label: 'Filing',
      icon: <DocumentChartBarIcon className="w-5 h-5" />,
      requiresPermission: 'REPORTS_FILING'
    },
  ];

  // Filter tabs based on user permissions
  const tabs = useMemo(() => {
    // Admin sees all tabs
    if (isAdmin) {
      console.log('[ReportsPage] User is admin - showing all tabs');
      return allTabs;
    }

    // Filter tabs based on permissions
    const filteredTabs = allTabs.filter(tab => {
      // For specific report types, check individual permissions ONLY
      // Do NOT check for general REPORTS permission - only specific report permissions
      let hasAccess = false;

      switch (tab.id) {
        case 'overview':
          hasAccess = hasPermission('REPORTS_OVERVIEW', 'VIEW');
          break;
        case 'clients':
          hasAccess = hasPermission('REPORTS_CLIENTS', 'VIEW');
          break;
        case 'revenue':
          hasAccess = hasPermission('REPORTS_REVENUE', 'VIEW');
          break;
        case 'ingredients':
          hasAccess = hasPermission('REPORTS_INGREDIENTS', 'VIEW');
          break;
        case 'diets':
          hasAccess = hasPermission('REPORTS_DIETS', 'VIEW');
          break;
        case 'count':
          // Chef Count Report - check for specific permission
          hasAccess = hasPermission('REPORTS_CHEF_COUNT', 'VIEW');
          break;
        case 'filing':
          // Filing Report - check for specific permission
          hasAccess = hasPermission('REPORTS_FILING', 'VIEW');
          break;
        default:
          hasAccess = false;
      }

      console.log(`[ReportsPage] Tab "${tab.id}": ${hasAccess ? 'VISIBLE' : 'HIDDEN'}`);
      return hasAccess;
    });

    console.log(`[ReportsPage] Total visible tabs: ${filteredTabs.length}`, filteredTabs.map(t => t.id));
    return filteredTabs;
  }, [isAdmin, hasPermission, allTabs]);

  // Update active tab if current tab is not available
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id as ReportTab);
    }
  }, [tabs, activeTab]);

  const handleExportReport = async () => {
    try {
      const blob = await analyticsService.exportReport(activeTab, dateRange);
      const filename = `${activeTab}-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      analyticsService.downloadReport(blob, filename);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <StatsCard stats={getStatsCardData()} />
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-soft p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Growth</h3>
                <ClientAnalytics type="growth" dateRange={dateRange} />
              </div>

              <div className="bg-white rounded-lg shadow-soft p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h3>
                <RevenueAnalytics type="trend" dateRange={dateRange} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-soft p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Ingredients</h3>
                <IngredientAnalytics type="popular" dateRange={dateRange} />
              </div>

              <div className="bg-white rounded-lg shadow-soft p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Diet Distribution</h3>
                <DietAnalytics type="distribution" dateRange={dateRange} />
              </div>
            </div>
          </div>
        );

      case 'clients':
        return <ClientAnalytics type="detailed" dateRange={dateRange} />;

      case 'revenue':
        return <RevenueAnalytics type="detailed" dateRange={dateRange} />;

      case 'ingredients':
        return <IngredientAnalytics type="detailed" dateRange={dateRange} />;

      case 'diets':
        return <DietAnalytics type="detailed" dateRange={dateRange} />;

      case 'count':
        return <CountReport />;

      case 'filing':
        return <FilingReport />;

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600">Comprehensive insights into your diet station operations</p>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Date Range Selector */}
          <div className="flex items-center space-x-2">
            <CalendarIcon className="w-5 h-5 text-gray-400" />
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              className="form-input text-sm"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              className="form-input text-sm"
            />
          </div>
          
          {activeTab !== 'menu-preparation' && activeTab !== 'daily-production' && (
            <Button
              onClick={handleExportReport}
              variant="secondary"
              className="flex items-center"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Export Report
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      {tabs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-soft p-12 text-center">
          <div className="max-w-md mx-auto">
            <ChartBarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Reports Available</h3>
            <p className="text-gray-600">
              You don't have permission to view any reports. Please contact your administrator to request access.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as ReportTab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
